import * as React from 'react';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Alert, Box, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import NumberFormat from 'react-number-format';
import moment from 'moment';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import QRStyledCard from 'ui-component/QRStyledCard';
import ApiLink from 'API';

const QR_TTL_MS = 24 * 60 * 60 * 1000; // BCEL QR valid for 24h

// Ticking countdown — green when fresh, orange < 1h, red when expired.
function ApprovedCountdown({ updatedAt }) {
    const [now, setNow] = React.useState(Date.now());
    React.useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const start = updatedAt ? new Date(updatedAt).getTime() : Date.now();
    const remaining = Math.max(0, start + QR_TTL_MS - now);
    const h = Math.floor(remaining / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    const s = Math.floor((remaining % 60000) / 1000);
    const expired = remaining === 0;
    const urgent = remaining < 3600000;
    const color = expired ? '#c62828' : urgent ? '#f57224' : '#2e7d32';
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                bgcolor: expired ? '#ffebee' : urgent ? '#fff4e5' : '#e8f5e9',
                border: `1px solid ${color}`
            }}
        >
            <AccessTimeIcon sx={{ color, fontSize: 22 }} />
            <Box>
                <Typography variant="caption" sx={{ color, fontWeight: 600, display: 'block', lineHeight: 1 }}>
                    QR expires in
                </Typography>
                <Typography
                    variant="h4"
                    sx={{ fontFamily: 'monospace', fontWeight: 800, color, letterSpacing: 1, lineHeight: 1.2 }}
                >
                    {expired ? 'EXPIRED' : `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`}
                </Typography>
            </Box>
        </Box>
    );
}

const statusColor = {
    pending: '#f57224',
    waitapproval: '#0c4da2',
    approved: '#2e7d32',
    paid: '#1b5e20',
    cancelled: '#c62828',
    timeout: '#616161'
};

export default function ViewQrPaymentDetail({ Open, setOpen, SelectedData, onUpdated }) {
    const handleClose = () => setOpen(false);
    const order = SelectedData?.order || {};

    // Realtime status: socket pushes 'paymentSuccess' to the chanel room; we also
    // expose a manual check button that hits BCEL via backend.
    const [liveStatus, setLiveStatus] = React.useState(null);
    const [checking, setChecking] = React.useState(false);
    const [checkMsg, setCheckMsg] = React.useState(null);
    const [confirmCancelOpen, setConfirmCancelOpen] = React.useState(false);
    const [cancelling, setCancelling] = React.useState(false);

    React.useEffect(() => {
        // Reset transient state every time a different doc opens.
        setLiveStatus(null);
        setCheckMsg(null);
        setConfirmCancelOpen(false);
        setCancelling(false);
    }, [SelectedData?._id, Open]);

    // Subscribe to the qrPayment's chanel room while the dialog is open and
    // the payment is still pollable. Disconnect cleanly on close/unmount.
    React.useEffect(() => {
        if (!Open) return undefined;
        const chanel = SelectedData?.chanel;
        const pollable = ['approved', 'pending'].includes(SelectedData?.status);
        if (!chanel || !pollable) return undefined;

        const socket = socketIO(ApiLink, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
        });

        socket.on('connect', () => {
            socket.emit('join_payment_room', { chanel });
        });
        socket.on('paymentSuccess', (payload) => {
            setLiveStatus('paid');
            setCheckMsg({ severity: 'success', text: `✅ ການຊຳລະເງິນສຳເລັດ! (${payload?.totalAmount?.toLocaleString?.() || ''} LAK)` });
        });
        socket.on('paymentError', (payload) => {
            if (payload?.status) setLiveStatus(payload.status);
        });

        return () => {
            socket.disconnect();
        };
    }, [Open, SelectedData?._id, SelectedData?.chanel, SelectedData?.status]);

    const handleCheckPayment = async () => {
        if (!SelectedData?._id) return;
        setChecking(true);
        setCheckMsg(null);
        try {
            const res = await axios.post(
                `${ApiLink}/api/stockcontroller/salepreorder/qrpayment/check-payment/${SelectedData._id}`,
                {},
                { withCredentials: true, headers: { token: localStorage.getItem('token') } },
            );
            if (res.data?.status) setLiveStatus(res.data.status);
            if (res.data?.success && res.data?.status === 'paid') {
                setCheckMsg({ severity: 'success', text: '✅ ການຊຳລະເງິນສຳເລັດ!' });
            } else {
                setCheckMsg({ severity: 'info', text: res.data?.msg || `Status: ${res.data?.status || 'pending'}` });
            }
        } catch (err) {
            setCheckMsg({ severity: 'error', text: err?.response?.data?.msg || err.message });
        }
        setChecking(false);
    };

    const effectiveStatus = liveStatus || SelectedData?.status;
    const isPaid = effectiveStatus === 'paid';
    // Only allow cancel + check-payment once the QR has been approved (so it's
    // out with the customer). While status is 'pending' or 'waitapproval' the
    // QR is still in the finance/approval queue — those flows have their own
    // verify/reject buttons elsewhere.
    const isCancellable = effectiveStatus === 'approved';
    const isCheckable = effectiveStatus === 'approved';

    const handleConfirmCancel = async () => {
        if (!SelectedData?._id) return;
        setCancelling(true);
        try {
            const res = await axios.put(
                `${ApiLink}/api/aonewallet/qrpayment/cancel/${SelectedData._id}`,
                {},
                { withCredentials: true, headers: { token: localStorage.getItem('token') } },
            );
            if (res.data?.success) {
                setLiveStatus('cancelled');
                setCheckMsg({ severity: 'success', text: '❌ Payment cancelled. Customer notified via WhatsApp.' });
                setConfirmCancelOpen(false);
                if (typeof onUpdated === 'function') onUpdated();
            } else {
                setCheckMsg({ severity: 'error', text: res.data?.msg || 'Cancel failed' });
            }
        } catch (err) {
            setCheckMsg({ severity: 'error', text: err?.response?.data?.msg || err.message });
        }
        setCancelling(false);
    };

    const handleDownloadQR = async () => {
        const brandedUrl = SelectedData?._id
            ? `${ApiLink}/api/aonewallet/qrpayment/qr-image/${SelectedData._id}.png`
            : SelectedData?.qrText;
        if (!brandedUrl) return;
        const filename = `QR_${SelectedData?.shortId || 'payment'}_${SelectedData?.customerID || ''}.png`;
        try {
            const response = await fetch(brandedUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const link = document.createElement('a');
            link.href = brandedUrl;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <Dialog open={Open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={2} alignItems="center">
                    <QrCode2Icon color="primary" />
                    <Typography variant="h4">QR Payment Detail</Typography>
                    {effectiveStatus && (
                        <Chip
                            label={effectiveStatus}
                            size="small"
                            icon={isPaid ? <CheckCircleIcon sx={{ color: '#fff !important' }} /> : undefined}
                            sx={{
                                backgroundColor: statusColor[effectiveStatus] || '#888',
                                color: '#fff',
                                fontWeight: 700
                            }}
                        />
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={1.5}>
                    {SelectedData.status === 'approved' && (
                        <ApprovedCountdown updatedAt={SelectedData.updatedAt || SelectedData.createdAt} />
                    )}
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Invoice (shortId):</Typography>
                        <Typography variant="button">{SelectedData?.shortId}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>CustomerID:</Typography>
                        <Typography variant="button" color="#0c4da2">{SelectedData?.customerID}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Invoice No:</Typography>
                        <Typography variant="caption">{order.invoiceNumber || '-'}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Total Amount:</Typography>
                        <Typography variant="h4" color="#0c4da2">
                            <NumberFormat
                                thousandsGroupStyle="thousand"
                                value={SelectedData.totalAmount}
                                prefix="₭"
                                decimalSeparator="."
                                displayType="text"
                                thousandSeparator={true}
                            />
                        </Typography>
                    </Stack>
                    {(order.credit || 0) > 0 && (
                        <Stack direction="row" spacing={2}>
                            <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Credit:</Typography>
                            <Typography variant="h4" color="#f57224">
                                <NumberFormat
                                    thousandsGroupStyle="thousand"
                                    value={order.credit}
                                    prefix="₭"
                                    decimalSeparator="."
                                    displayType="text"
                                    thousandSeparator={true}
                                />
                            </Typography>
                        </Stack>
                    )}
                    {(order.aoneWalletAmount || 0) > 0 && (
                        <Stack direction="row" spacing={2}>
                            <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Wallet:</Typography>
                            <Typography variant="h4" color="#3035BC">
                                <NumberFormat
                                    thousandsGroupStyle="thousand"
                                    value={order.aoneWalletAmount}
                                    prefix="₭"
                                    decimalSeparator="."
                                    displayType="text"
                                    thousandSeparator={true}
                                />
                            </Typography>
                        </Stack>
                    )}
                    <Divider />
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Created By:</Typography>
                        <Typography variant="button">{SelectedData?.sessionData?.fullname || '-'}</Typography>
                    </Stack>
                    {/* <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Branch:</Typography>
                        <Typography variant="button">{SelectedData?.sessionData?.branchCode || '-'}</Typography>
                    </Stack> */}
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>WhatsApp:</Typography>
                        <Typography variant="button">{SelectedData?.customerWhatsApp || '-'}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="subtitle2" color="gray" sx={{ minWidth: 120 }}>Created At:</Typography>
                        <Typography variant="caption" color="gray">
                            {SelectedData.createdAt ? moment(SelectedData.createdAt).format('DD-MM-YYYY HH:mm') : '-'}
                        </Typography>
                    </Stack>
                    {(SelectedData?.emvRaw || SelectedData?.qrText) && (
                        <>
                            <Divider />
                            <Stack alignItems="center" spacing={1}>
                                <Typography variant="subtitle2" color="gray">QR Code Preview</Typography>
                                {SelectedData?.emvRaw ? (
                                    <Box
                                        onClick={handleDownloadQR}
                                        sx={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            transition: 'transform 0.15s ease',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <QRStyledCard
                                            qrString={SelectedData.emvRaw}
                                            mcname="A-One Dristributors Co.,LTD"
                                            username={SelectedData?.customerID || SelectedData?.shortId || 'CUSTOMER'}
                                            size={240}
                                        />
                                        {isPaid && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                                                    borderRadius: 2,
                                                    pointerEvents: 'none',
                                                    backdropFilter: 'blur(10px)',
                                                    WebkitBackdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 96,
                                                        height: 96,
                                                        borderRadius: '50%',
                                                        border: '6px solid #22c55e',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: 'rgba(255,255,255,0.6)',
                                                    }}
                                                >
                                                    <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e' }} />
                                                </Box>
                                                <Typography
                                                    variant="h3"
                                                    sx={{
                                                        mt: 2,
                                                        fontWeight: 800,
                                                        color: '#15803d',
                                                    }}
                                                >
                                                    ໄດ້ຮັບເງິນແລ້ວ
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box
                                            component="img"
                                            src={SelectedData.qrText}
                                            alt="QR Code"
                                            onClick={handleDownloadQR}
                                            sx={{
                                                width: 220,
                                                height: 220,
                                                border: '1px solid #ddd',
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                '&:hover': { transform: 'scale(1.02)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                                            }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                        {isPaid && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                                                    borderRadius: 2,
                                                    pointerEvents: 'none',
                                                    backdropFilter: 'blur(10px)',
                                                    WebkitBackdropFilter: 'blur(10px)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: '50%',
                                                        border: '5px solid #22c55e',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        bgcolor: 'rgba(255,255,255,0.6)',
                                                    }}
                                                >
                                                    <CheckCircleIcon sx={{ fontSize: 52, color: '#22c55e' }} />
                                                </Box>
                                                <Typography variant="h4" sx={{ mt: 1.5, fontWeight: 800, color: '#15803d' }}>
                                                    ໄດ້ຮັບເງິນແລ້ວ
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                <Typography variant="caption" color="textSecondary">click to download branded QR PNG</Typography>
                                {isCheckable && (
                                    <Stack spacing={1} sx={{ width: '100%', mt: 1 }} alignItems="center">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={checking ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                                            onClick={handleCheckPayment}
                                            disabled={checking || isPaid}
                                            sx={{ minWidth: 220 }}
                                        >
                                            {checking ? 'Checking...' : 'Check Payment Status'}
                                        </Button>
                                        <Typography variant="caption" color="textSecondary">
                                            ກວດສະຖານະການຊຳລະທີ່ BCEL OnePay
                                        </Typography>
                                    </Stack>
                                )}
                                {checkMsg && (
                                    <Alert severity={checkMsg.severity} sx={{ width: '100%', mt: 1 }}>
                                        {checkMsg.text}
                                    </Alert>
                                )}
                            </Stack>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                {isCancellable && (
                    <Button
                        onClick={() => setConfirmCancelOpen(true)}
                        variant="contained"
                        startIcon={<CancelIcon />}
                        disabled={cancelling}
                        sx={{
                            backgroundColor: '#c62828',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#9a1a1a' },
                        }}
                    >
                        Cancel Payment
                    </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={handleClose} color="primary" variant="outlined">Close</Button>
            </DialogActions>

            <Dialog open={confirmCancelOpen} onClose={() => !cancelling && setConfirmCancelOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CancelIcon color="error" />
                        <Typography variant="h4">Confirm Cancel Payment</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5}>
                        <Typography variant="body1">
                            ຕ້ອງການຍົກເລີກ QR Payment ນີ້ບໍ່? ລູກຄ້າຈະໄດ້ຮັບການແຈ້ງເຕືອນທາງ WhatsApp.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Typography variant="subtitle2" color="gray">Invoice:</Typography>
                            <Typography variant="button">{SelectedData?.shortId}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Typography variant="subtitle2" color="gray">CustomerID:</Typography>
                            <Typography variant="button" color="#0c4da2">{SelectedData?.customerID}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Typography variant="subtitle2" color="gray">WhatsApp:</Typography>
                            <Typography variant="button">{SelectedData?.customerWhatsApp || '-'}</Typography>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCancelOpen(false)} disabled={cancelling} color="secondary">
                        No
                    </Button>
                    <Button
                        onClick={handleConfirmCancel}
                        disabled={cancelling}
                        variant="contained"
                        startIcon={cancelling ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CancelIcon />}
                        sx={{
                            backgroundColor: '#c62828',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#9a1a1a' },
                        }}
                    >
                        {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
